// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
import { isEntityAction } from "@ms/planner-actions/lib/local/utils/ActionUtils";
import { TableName } from "@ms/uno-constants/lib/local/StoreConstants";
// Entity
import { applyComparator } from "@ms/uno-entitycore/lib/local/table/EntityTable";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { EntitySlice } from "../../slice/EntitySlice";
import { Store } from "../Store";
import { compareByOrderHint } from "@ms/uno-utilities/lib/local/Comparators";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { filterPersistedTablesForStore } from "../../utilities/StoreUtilities";
/**
 * Implementation of IBucketStore using EntitySlice
 */ export class BucketStore extends EventPublishingStore {
    get name() {
        return Store.Bucket;
    }
    getPersistedTables() {
        return filterPersistedTablesForStore(Store.Bucket, {
            [TableName.Bucket]: this.bucketSlice.table(),
            ...this.bucketSlice.statusTables()
        });
    }
    getBucket(id, readBaseline) {
        return this.bucketSlice.get(id, readBaseline);
    }
    getBucketsForPlan(planId, readBaseline) {
        return this.bucketSlice.getCollection({
            name: QueryRelationship.BucketsInPlan,
            value: planId
        }, readBaseline);
    }
    getBaselineDelta(id, deltaKey, isForward) {
        return this.bucketSlice.getBaselineDelta(id, deltaKey, isForward);
    }
    handleAction(action) {
        let storeChanged = false;
        if (!this.shouldHandleAction(action)) {
            return;
        }
        switch(action.type){
            case ActionType.FetchEntityAction:
                storeChanged = this.bucketSlice.handleFetchStarted(action.entityId);
                break;
            case ActionType.FetchEntityFailedAction:
                storeChanged = this.bucketSlice.handleFetchFailed(action.entityId, action.response);
                break;
            case ActionType.FetchEntitySucceededAction:
                storeChanged = this.bucketSlice.handleFetchSucceeded(action.entity.id, action.entity, action.forceOverrideStoredData);
                break;
            case ActionType.FetchEntityCollectionAction:
                // ideally, we should generate a new index for the parent type
                if (action.parentType === EntityType.Plan) {
                    storeChanged = this.bucketSlice.handleCollectionFetchStarted({
                        name: action.relationship,
                        value: action.parentId
                    });
                }
                break;
            case ActionType.FetchEntityCollectionFailedAction:
                if (action.parentType === EntityType.Plan) {
                    storeChanged = this.bucketSlice.handleCollectionFetchFailed({
                        name: action.relationship,
                        value: action.parentId
                    }, action.response);
                }
                break;
            case ActionType.FetchEntityCollectionSucceededAction:
                if (action.parentType === EntityType.Plan) {
                    storeChanged = this.bucketSlice.handleCollectionFetchSucceeded({
                        name: action.relationship,
                        value: action.parentId
                    }, true, Object.fromEntries(action.entities.map((item)=>[
                            item.id,
                            item
                        ])), [], action.deltaLink, action.forceOverrideStoredData);
                }
                break;
            case ActionType.CreateEntityAction:
                storeChanged = this.bucketSlice.handleCreateStarted(action.entity.id, action.entity);
                break;
            case ActionType.CreateEntityFailedAction:
                storeChanged = this.bucketSlice.handleCreateFailed(action.entity.id);
                break;
            case ActionType.CreateEntitySucceededAction:
                storeChanged = this.bucketSlice.handleCreateSucceeded(action.localId, action.entity, action.entity.id);
                break;
            case ActionType.DeleteEntityAction:
                storeChanged = this.bucketSlice.handleDeleteStarted(action.entityId);
                break;
            case ActionType.DeleteEntityFailedAction:
                storeChanged = this.bucketSlice.handleDeleteFailed(action.entityId, action.response, action.errorCode);
                break;
            case ActionType.DeleteEntitySucceededAction:
                storeChanged = this.bucketSlice.handleDeleteSucceeded(action.entityId);
                break;
            case ActionType.UpdateEntityAction:
                storeChanged = this.bucketSlice.handleUpdateStarted(action.entityId, action.updateKey, action.delta);
                break;
            case ActionType.UpdateEntityFailedAction:
                storeChanged = this.bucketSlice.handleUpdateFailed(action.entityId, action.updateKey, action.response, action.errorCode);
                break;
            case ActionType.UpdateEntitySucceededAction:
                storeChanged = this.bucketSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity);
                break;
            case ActionType.UndoRedoUpdateEntitySucceededAction:
                storeChanged = this.bucketSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity, true);
                break;
            case ActionType.DeltaSyncAction:
                storeChanged = this.bucketSlice.handleDeltaSyncStarted({
                    name: action.relationship,
                    value: action.parentId
                });
                break;
            case ActionType.DeltaSyncSucceededAction:
                storeChanged = this.bucketSlice.handleDeltaSyncSucceeded({
                    name: action.relationship,
                    value: action.parentId
                }, Object.fromEntries(action.deltas.map((item)=>[
                        item.entity.id,
                        item
                    ])), action.deltaLink);
                break;
            case ActionType.DeltaSyncFailedAction:
                storeChanged = this.bucketSlice.handleDeltaSyncFailed({
                    name: action.relationship,
                    value: action.parentId
                }, action.response, action.errorCode);
                break;
            case ActionType.SuspendEntityDeltaAction:
                storeChanged = this.bucketSlice.handleSuspendDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.ResumeEntityDeltaAction:
                storeChanged = this.bucketSlice.handleResumeDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.DropSuspendedDeltaAction:
                storeChanged = this.bucketSlice.handleDropSuspendedDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.PruneBaselineChainAction:
                storeChanged = this.bucketSlice.removeBaselineChainEntry(action.entityId, action.deltaKey);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    shouldHandleAction(action) {
        if (isEntityAction(action)) {
            // Only handle entity actions for buckets
            return action.entityType === EntityType.Bucket;
        }
        return true; // Handle all other actions
    }
    constructor(loggers, configProvider){
        super(), this.loggers = loggers, this.configProvider = configProvider, /**
     * Bucket partition structure
     *
     * ```
     * ☶ Root Table: All Buckets
     * └── BucketsInPlan // <-- Comparator applied at the root so that buckets are sorted by default for all queries
     *     ├── ☶ PlanA
     *     └── ☶ PlanB
     */ this.bucketSlice = new EntitySlice(TableName.Bucket, {
            /** Index for serving data queries for buckets in plan */ [QueryRelationship.BucketsInPlan]: (bucket)=>bucket.planId
        }, this.configProvider);
        // Apply order hint comparator to the root table, default sorting buckets by order hint descending
        applyComparator(this.bucketSlice.table(), (bucket1, bucket2)=>compareByOrderHint(bucket2, bucket1, (bucket)=>bucket.extensions.orderHint));
    }
}
